<script setup>
import { computed, inject, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';
import { useToast } from 'vue-toastification';
import { useSupportPlanEventStore } from '@/stores/supportPlanEvent';

const supportPlanEventStore = useSupportPlanEventStore();
const { searchSupportPlanEvents, createBlankEvent, deleteSupportPlanEvent, } = supportPlanEventStore;
const { supportPlanEvents } = storeToRefs(supportPlanEventStore);

const modals = inject('modals');
const toast = useToast();

const props = defineProps({
    supportPlan: {
        type: Object,
        default: () => null
    }
});

const params = ref({
    term: '',
    page: 1,
    per_page: 10,
});

const fetchSupportPlanEvents = () => {
    searchSupportPlanEvents({
        ...params.value,
        hash: props.supportPlan.hash,
    });
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    fetchSupportPlanEvents();
};

const createNewEvent = async () => {
    const payload = {
        support_plan_id: props.supportPlan.id,
    };
    const resp = await createBlankEvent(payload);

    const event = resp.data.event;

    window.location.href = `/clients/${props.supportPlan.client.hash}/support-plan/${props.supportPlan.hash}/events/${event.hash}`;
};

const gotoEvent = (item) => {
    window.location.href = `/clients/${props.supportPlan.client.hash}/support-plan/${props.supportPlan.hash}/events/${item.hash}`;
};

const destroyEvent = async (event) => {
    const firstConfirmation = await modals.show('ConfirmationModal', {
        props: {
            title: trans('label.delete_event'),
            prompt: trans('label.are_you_sure_delete_support_plan_event'),
        },
    });

    if (!firstConfirmation) {
        return;
    }

    const secondConfirmation = await modals.show('ConfirmationModal', {
        props: {
            title: trans('label.confirm_event_deletion'),
            prompt: trans('label.confirm_again_delete_support_plan_event'),
        },
    });

    if (!secondConfirmation) {
        return;
    }

    try {
        await deleteSupportPlanEvent(event.hash);

        if (supportPlanEvents.value.data.length === 1 && params.value.page > 1) {
            params.value.page -= 1;
        }

        fetchSupportPlanEvents();
        toast.success(trans('label.support_plan_event_deleted'));
    } catch (_) {
        toast.error(trans('label.error'));
    }
};

const eventHeaders = computed(() => [
    { title: trans('label.date'), key: 'date', value: item => `${item.date}` },
    { title: trans('label.block'), key: 'block', value: item => `${item.block}` },
    { title: trans('label.program'), key: 'program', value: item => `${item.program ? item.program.name : 'N/A'}` },
    { title: trans('label.highlight'), key: 'was_highlight', value: item => item.was_highlight ? trans('label.yes') : trans('label.no') },
]);
</script>

<template>
<div>
    <div class="flex justify-end pb-4">
        <StyledButton @click="createNewEvent()">
            <FontAwesomeIcon icon="plus" class="mr-1" />{{ $t('label.new_event') }}
        </StyledButton>
    </div>
    <DataTable
        v-if="supportPlanEvents && supportPlanEvents.data"
        :data="supportPlanEvents"
        :headers="eventHeaders"
        :actions="[
            {
                title: $t('label.edit'),
                icon: 'mdi-pencil',
                action: (item) => gotoEvent(item),
            },
            {
                title: $t('label.delete'),
                icon: 'mdi-delete',
                action: (item) => destroyEvent(item),
            }
        ]" 
        @table-update="tableUpdate" />
    <div v-else>
        {{ $t('label.no_support_plan_events') }}
    </div>
</div>
</template>
